import { Injectable, Injector, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, defer, finalize, from, map, Observable, of, shareReplay, Subject, Subscription, switchMap, take, timeout } from 'rxjs';
import { AuthUtils, isSignedOutRoute } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ApiResponse } from 'app/shared/common/models/apiResponse';
import { FirebaseApp } from '@angular/fire/app';
import { BizError } from 'app/shared/common/models/bizError';
import { stringify } from 'crypto-js/enc-base64';
import { TenantDto } from 'app/shared/apis/subscription/model/tenantDto';
import { UserDto } from 'app/shared/apis/user/model/userDto';
import { SubscriptionStatus } from 'app/shared/apis/subscription/model/subscriptionStatus';
import { Role } from 'app/shared/common/enums/role';
import { AppShellService } from 'app/core/app-shell/app-shell.service';
import firebase from 'firebase/compat/app';

/** SPEC-web-planner-027 AC-3: why a session ended; carried to /sign-in as `?reason=`. */
export type SignOutReason = 'idle' | 'expired';

/** SPEC-web-planner-027 AC-3: signed out after this long with no activity. */
export const IDLE_LIMIT_MS = 8 * 60 * 60 * 1000;
/** localStorage key holding the last activity time (epoch ms), shared between tabs. */
export const LAST_ACTIVITY_KEY = 'lastActivity';
/** Writes of the last-activity time are throttled to once a minute. */
const ACTIVITY_WRITE_EVERY_MS = 60 * 1000;
/** The idle check runs this often. */
const IDLE_CHECK_EVERY_MS = 60 * 1000;
/** SPEC-web-planner-027 AC-2: refresh a token that is expired or expires within this many seconds. */
export const REFRESH_WITHIN_SECONDS = 5 * 60;
/** If the SDK never reports its restored state, stop waiting after this long. */
const FIREBASE_READY_TIMEOUT_MS = 10000;

@Injectable()
export class AuthService implements OnDestroy {

    user$ = new Subject<UserDto>();
    /** Emits whenever the selected workspace is (re)set; PersonaService refetches User/Me on it. */
    selectedTenant$ = new Subject<TenantDto>();
    /** Emits after sign-out has cleared the session; PersonaService drops its cache on it. */
    signedOut$ = new Subject<void>();
    private _authenticated: boolean = false;

    /**
     * SPEC-web-planner-027 AC-2: completes on the first `afAuth.authState` emission, i.e. once the SDK has restored
     * (or found no) session from IndexedDB. After a reload `afAuth.currentUser` is null until then (firebase 9.10
     * has no authStateReady()). Gives up after a timeout so a request never hangs on it.
     */
    readonly firebaseReady$: Observable<void> = defer(() => this.afAuth.authState).pipe(
        take(1),
        timeout({ first: FIREBASE_READY_TIMEOUT_MS, with: () => of(null) }),
        map(() => undefined as void),
        shareReplay({ bufferSize: 1, refCount: false })
    );

    /** One refresh shared by every request that needs it. */
    private _refresh$: Observable<boolean> | null = null;
    /** SPEC-web-planner-027 AC-3: set by check() when it returned false because a token refresh failed. */
    private _checkFailure: SignOutReason | null = null;
    private _subscriptions = new Subscription();
    private _idleTimer: any = null;
    private _lastActivityWrite = 0;
    private _idleSigningOut = false;
    private _removeActivityListeners: (() => void) | null = null;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        public afAuth: AngularFireAuth,
        // Injector (not direct injection) breaks the AuthService ↔ AppShellService
        // DI cycle: AppShellService depends on AuthService, so we resolve it lazily
        // only inside signOut() to clear the persona cache.
        private _injector: Injector,
        // SPEC-web-planner-027: the activity listeners and the idle interval run outside Angular's zone.
        private _zone: NgZone
    ) {
        // SPEC-web-planner-027 AC-2 (Mirror): copy every new ID token the SDK reports into localStorage, but only while
        // an app session exists, so a cleared or refused session is never re-armed. A null emission is ignored.
        const idToken$ = this.afAuth.idToken;
        if (idToken$) {
            this._subscriptions.add(idToken$.subscribe((token) => {
                if (token && this.hasAppSession) {
                    localStorage.setItem('accessToken', token);
                }
            }));
        }
        this._startIdleWatch();
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
        this._removeActivityListeners?.();
        this._removeActivityListeners = null;
        if (this._idleTimer) { clearInterval(this._idleTimer); this._idleTimer = null; }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    set accessToken(token: string) {
        if (token !== '') {
            localStorage.setItem('accessToken', token);
        } else {
            localStorage.setItem('accessToken', this.firebaseUser.stsTokenManager.accessToken);
        }
    }

    /**
     * Setter & getter for user
     */
     // eslint-disable-next-line @typescript-eslint/member-ordering
     get firebaseUser(): any {
        const fbUserStr = localStorage.getItem('firebaseUser');
        return fbUserStr ? JSON.parse(fbUserStr) : '';
    }

    set firebaseUser(firebaseUser: any) {
        localStorage.setItem('firebaseUser', JSON.stringify(firebaseUser));
    }
    /**
     * Setter & getter for access token
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    get user(): UserDto {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : undefined;
    }

    set user(user: UserDto) {
        this.user$.next(user);
        localStorage.setItem('user', JSON.stringify(user));
    }

    /** An app session exists: the app's own user record is stored (the Firebase session alone does not count). */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    get hasAppSession(): boolean {
        return !!localStorage.getItem('user');
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    get roles(): Role[] {
        return this.user.tenants.filter(tenant => tenant.id === this.selectedTenant.id)[0].roles;
    }

    /**
     * Setter & getter for access token
     */
     // eslint-disable-next-line @typescript-eslint/member-ordering
     get selectedTenant(): TenantDto {
        const tenantStr = localStorage.getItem('tenant');
        return tenantStr ? JSON.parse(tenantStr) : undefined;
    }

    set selectedTenant(tenant: TenantDto) {
        localStorage.setItem('tenant', JSON.stringify(tenant));
        this.selectedTenant$.next(tenant);
    }

    /**
     * SPEC-web-planner-035 AC-3: stores a renamed organisation on the selected tenant when its id matches. Unlike the
     * selectedTenant setter it does not emit selectedTenant$ (no persona refresh, seat-read clear or activity-share drop),
     * but a reload still reads the new name. The matching entry's `name` in the stored user's tenants[] is updated too
     * (written straight to storage, not through the user setter, so user$ does not emit), for readers that prefer it.
     */
    renameSelectedTenant(id: string, organization: string): void {
        const tenant: any = this.selectedTenant;
        if (!tenant || !id || tenant.id !== id) { return; }
        localStorage.setItem('tenant', JSON.stringify({ ...tenant, organization }));
        const user: any = this.user;
        if (user?.tenants?.some((t: any) => t?.id === id)) {
            localStorage.setItem('user', JSON.stringify({
                ...user,
                tenants: user.tenants.map((t: any) => (t?.id === id ? { ...t, name: organization } : t)),
            }));
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    async forgotPassword(email: string): Promise<any> {

        this._httpClient.post('api/auth/forgot-password', email);
        return await this.afAuth
            .sendPasswordResetEmail(email)
            .then(() => {
                return true;
            })
            .catch((error) => {
                console.log(error);
                return false;
            });
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }
 /**
  * Change password
  *
  * @param password
  */
    // changePassword(code, password: string): Promise<ApiResponse<any>> {
    //     const response = new ApiResponse();
    //     return this.afAuth.confirmPasswordReset(code, password)
    //     .then((result) => { // Store the access token in the local storage
    //         console.log(result);
    //     // const userData = JSON.parse(JSON.stringify(result));
    //     // if (!userData.emailVerified) {
    //     //     return this.constructError(response, 'Please verify your email before login!', 'Email verification should be completed before the user tries to login.');
    //     // }
    //     response.isSuccess = true;
    //     response.result = result;
    //     return response;

    // }).catch(error => {
    //     console.log('Error in firebase user reset password!');
    //     console.log(error.message);
    //     return this.constructError(response, error.message, 'Error in firebase user reset password!');
    //     });

    // }
    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Promise<ApiResponse<any>> {
        // Throw error, if the user is already logged in
        // if (this._authenticated) {
        //     return throwError('User is already logged in.');
        // }

        // return this._httpClient.post('api/auth/sign-in', credentials).pipe(
        //     switchMap((response: any) => {

        //         // Store the access token in the local storage
        //         this.accessToken = response.accessToken;

        //         // Set the authenticated flag to true
        //         this._authenticated = true;

        //         // Store the user on the user service
        //         this._userService.user = response.user;

        //         // Return a new observable with the response
        //         return of(response);
        //     })
        // );
        const response = new ApiResponse();

        return this.afAuth
            .signInWithEmailAndPassword(credentials.email, credentials.password)
            .then((result) => {

                // Store the access token in the local storage
                const userData = JSON.parse(JSON.stringify(result.user));
                // if (!userData.emailVerified) {
                //     return this.constructError(response, 'Please verify your email before login!', 'Email verification should be completed before the user tries to login.');
                // }
                this.accessToken = userData.stsTokenManager.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the local storage
                this.firebaseUser = result.user;

                response.isSuccess = true;
                response.result = result;
                return response;
            })
            .catch((error) => {
                console.log('Firebase sign-in failed');
                console.log(error.message);
                return this.constructError(response, error.message, 'Firebase sign-in failed');
            });
    }

    /**
     * Sign in with Google SSO. Same ApiResponse contract as signIn().
     * AR-05: scoped to .awvent.com via Firebase authDomain.
     */
    signInWithGoogle(): Promise<ApiResponse<any>> {
        const response = new ApiResponse();
        const provider = new firebase.auth.GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });

        return this.afAuth
            .signInWithPopup(provider)
            .then((result) => {
                const userData = JSON.parse(JSON.stringify(result.user));
                this.accessToken = userData.stsTokenManager.accessToken;
                this._authenticated = true;
                this.firebaseUser = result.user;

                response.isSuccess = true;
                response.result = result;
                return response;
            })
            .catch((error) => {
                console.log('Error in Google sign-in!');
                console.log(error.message);
                return this.constructError(response, error.message, 'Error in Google sign-in!');
            });
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient.post('api/auth/sign-in-with-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>

                // Return false
                of(false)
            ),
            switchMap((response: any) => {

                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    async signOut(): Promise<any> {

        try {
            await this.afAuth.signOut();
        } catch (e) {
            // SPEC-web-planner-027: a failed SDK sign-out must still clear this browser's session.
            console.warn('[auth] the Firebase sign-out failed; clearing the local session anyway', e);
        }
        {
            // Remove the access token (idToken) from local storage
            localStorage.removeItem('accessToken');

            localStorage.removeItem('firebaseUser');
            localStorage.removeItem('user');
            localStorage.removeItem(LAST_ACTIVITY_KEY);
            // Clear tenant header context
            localStorage.removeItem('tenant');

            // Clear the persona cache via the resolver-owned clear method (v2
            // cache key from SESSION-001c) — do NOT reach into localStorage for
            // the persona key directly. Also drop the legacy v1 `personaRole`
            // key the demo chooser used to stamp, so logout always lands clean
            // on State 1 with no stale persona. SESSION-meet-ux-002.
            this._injector.get(AppShellService).clearPersona();
            localStorage.removeItem('personaRole');

            // Set the authenticated flag to false
            this._authenticated = false;
            this.signedOut$.next();
        }

        // Return the observable
        return of(true);
    }

    /**
     * SPEC-web-planner-027 AC-2: make sure the stored token is fresh before a request. Refreshes only when ALL hold:
     * a non-empty stored token, an app session (`user`), a token that is expired or expires within 5 minutes, and a
     * Firebase user once the SDK has restored its session. With no stored token or no `user` it never refreshes and
     * stores nothing, so a cleared or refused session cannot come back through a request. Concurrent callers share
     * one refresh. Emits once: true when nothing was needed or the refresh worked, false when it failed.
     */
    refreshIfNeeded(): Observable<boolean> {
        if (!this._needsRefresh()) {
            return of(true);
        }
        if (!this._refresh$) {
            this._refresh$ = this.firebaseReady$.pipe(
                // compat `currentUser` is a Promise
                switchMap(() => from(this.afAuth.currentUser)),
                switchMap((fbUser) => fbUser ? from(fbUser.getIdToken(true)) : of(null)),
                map((token) => {
                    if (!token || !this.hasAppSession) {
                        return false;
                    }
                    localStorage.setItem('accessToken', token);
                    return true;
                }),
                catchError(() => of(false)),
                finalize(() => { this._refresh$ = null; }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }
        return this._refresh$.pipe(take(1));
    }

    /** The reason the last check() returned false, if a failed refresh caused it; reading clears it. */
    takeCheckFailure(): SignOutReason | null {
        const reason = this._checkFailure;
        this._checkFailure = null;
        return reason;
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        this._checkFailure = null;
        // SPEC-web-planner-027 AC-3: a session idle for more than 8 hours (a browser reopened or woken late) is ended
        // here, before it is accepted or its token refreshed.
        if (this.isIdleExpired()) {
            this._endIdleSession(false);
            this._checkFailure = 'idle';
            return of(false);
        }
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date. SPEC-web-planner-027 AC-2: an expired stored token is refreshed when an
        // app session exists (the SDK keeps a refresh token); it never reaches signInUsingToken() while expired.
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            if (!this.hasAppSession) {
                return of(false);
            }
            return this.refreshIfNeeded().pipe(
                switchMap((ok) => {
                    if (ok && !AuthUtils.isTokenExpired(this.accessToken)) {
                        return this.signInUsingToken();
                    }
                    this._checkFailure = 'expired';
                    return of(false);
                })
            );
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    checkForTenant(): Observable<boolean> {

        if (this.selectedTenant === undefined) {
            return of(false);
        }

        if(this.selectedTenant.subscriptions === undefined ||
            (this.selectedTenant.subscriptions !== undefined && this.selectedTenant.subscriptions.length === 0)) {
            return of(false);
        } else if (this.selectedTenant.subscriptions.length > 0) {
            let activeSubscriptionFound = false;
            this.selectedTenant.subscriptions.forEach((subscription) => {
                switch (subscription.status) {
                    case SubscriptionStatus.Active:
                    case SubscriptionStatus.ScheduledForCancellation:
                    case SubscriptionStatus.TrialStarted:
                    case SubscriptionStatus.TrailExtensionStarted:
                        activeSubscriptionFound = true;
                        break;
                }
            });

            if (activeSubscriptionFound) {
                return of(true);
            } else {
                return of(false);
            }
        }
    }

    constructError(response: ApiResponse<any>, errorMsg: string, description: string): ApiResponse<any> {

        try {
            response.isSuccess = false;
            response.errors = [];

            const errorObj = new BizError();
            errorObj.message = errorMsg;
            errorObj.description = description;

            response.errors.push(errorObj);
            return response;
        } catch(ex) {
            console.error('Error while constructring error response!');
        }
    }

    authenticate(): void {

        try {
            if (localStorage.getItem('firebaseUser') !== '' && localStorage.getItem('accessToken') !== '') {
                this._authenticated = true;
            } else {
                this._authenticated = false;
            }
        } catch(ex) {
            console.error('Error while constructring error response!');
        }

    }

    /**
     * @param endFirebaseSession SPEC-web-planner-027 AC-2: also end the SDK session. Every sign-in refusal passes
     *        true, so the SDK does not restore a user the app refused after a reload.
     */
    clearStorage(endFirebaseSession = false): void {

        try {
            this._authenticated = false;
            localStorage.clear();
            if (endFirebaseSession) {
                Promise.resolve(this.afAuth.signOut()).catch(() => undefined);
            }
            // if (localStorage.getItem('firebaseUser') !== '' && localStorage.getItem('accessToken') !== '') {
            //     this._authenticated = true;
            // } else {
            //     this._authenticated = false;
            // }
        } catch(ex) {
            console.error('Error while constructring error response!');
        }

    }

    updateFirebaseUserEmailVerification(isEmailVerified: boolean): void {
        const firebaseUser = this.firebaseUser;
        firebaseUser.emailVerified = isEmailVerified;
        this.firebaseUser = firebaseUser;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Idle sign-out (SPEC-web-planner-027 AC-3)
    // -----------------------------------------------------------------------------------------------------

    /** An app session exists and its stored last activity is older than the idle limit. */
    isIdleExpired(now: number = Date.now()): boolean {
        if (!this.hasAppSession) {
            return false;
        }
        const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
        const last = raw === null ? NaN : Number(raw);
        return isFinite(last) && now - last > IDLE_LIMIT_MS;
    }

    /**
     * Sign out after IDLE_LIMIT_MS without activity (pointer, key, touch, or the tab becoming visible). Runs only
     * while an app session exists and never on the signed-out routes. Returns true when it signed out.
     * On a wake from sleep the check runs before the visibility is recorded as activity.
     */
    checkIdle(now: number = Date.now()): boolean {
        if (!this.hasAppSession || this._idleSigningOut || isSignedOutRoute(this._currentUrl())) {
            return false;
        }
        if (localStorage.getItem(LAST_ACTIVITY_KEY) === null || !isFinite(Number(localStorage.getItem(LAST_ACTIVITY_KEY)))) {
            // No value yet counts as "now".
            localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
            return false;
        }
        if (!this.isIdleExpired(now)) {
            return false;
        }
        this._endIdleSession(true);
        return true;
    }

    /** Ends an idle session (back inside Angular's zone); navigates to /sign-in?reason=idle when asked. */
    private _endIdleSession(navigate: boolean): void {
        if (this._idleSigningOut) { return; }
        this._idleSigningOut = true;
        this._zone.run(() => {
            this.signOut()
                .catch(() => undefined)
                .finally(() => {
                    this._idleSigningOut = false;
                    if (navigate) {
                        this._injector.get(Router).navigate(['/sign-in'], { queryParams: { reason: 'idle' } });
                    }
                });
        });
    }

    /**
     * Record activity, at most once a minute (the throttle is checked before any storage or router read). When the
     * stored value is stale the idle check runs first, so activity after a long absence signs out instead of
     * resetting the clock. No session, or a signed-out route, records nothing.
     */
    recordActivity(now: number = Date.now(), force = false): void {
        if (!force && now - this._lastActivityWrite < ACTIVITY_WRITE_EVERY_MS) {
            return;
        }
        if (!this.hasAppSession || isSignedOutRoute(this._currentUrl())) {
            return;
        }
        if (this.checkIdle(now)) {
            return;
        }
        this._lastActivityWrite = now;
        localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    }

    private _currentUrl(): string {
        try {
            const url = this._injector.get(Router)?.url;
            return typeof url === 'string' ? url : '';
        } catch {
            return '';
        }
    }

    private _startIdleWatch(): void {
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return;
        }
        const onActivity = (): void => this.recordActivity();
        const onVisibility = (): void => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            // Idle check first, then record the visibility as activity.
            if (!this.checkIdle()) {
                this.recordActivity(Date.now(), true);
            }
        };
        const events = ['pointerdown', 'pointermove', 'keydown', 'touchstart'];
        // Outside Angular's zone: pointermove and the interval must not trigger change detection.
        this._zone.runOutsideAngular(() => {
            events.forEach((e) => document.addEventListener(e, onActivity, { capture: true, passive: true }));
            document.addEventListener('visibilitychange', onVisibility);
            this._idleTimer = setInterval(() => this.checkIdle(), IDLE_CHECK_EVERY_MS);
        });
        this._removeActivityListeners = () => {
            events.forEach((e) => document.removeEventListener(e, onActivity, { capture: true }));
            document.removeEventListener('visibilitychange', onVisibility);
        };
        // A browser reopened after a long absence: end a stale session now, not at the first pointer move.
        this.checkIdle();
    }

    private _needsRefresh(): boolean {
        const token = this.accessToken;
        if (!token || !this.hasAppSession) {
            return false;
        }
        try {
            return AuthUtils.isTokenExpired(token, REFRESH_WITHIN_SECONDS);
        } catch {
            return false; // a token we cannot read is left to the 401 path
        }
    }
}
